import React, { useState, useRef, useEffect } from 'react';
import { motion, useScroll, useTransform, useInView, AnimatePresence, useMotionValue, useSpring } from 'framer-motion';

/* ── Design tokens (matches Home.tsx) ── */
const T = {
  teal:         '#5EEAD4',
  tealDark:     '#14B8A6',
  lavender:     '#C4B5FD',
  lavenderDark: '#8B5CF6',
  coral:        '#FDA4AF',
  coralDark:    '#F43F5E',
  text:         '#1E293B',
  textMuted:    '#64748B',
  textLight:    '#94A3B8',
};

/* ── Glass card ── */
const GlassCard: React.FC<{ children: React.ReactNode; className?: string; heavy?: boolean }> = ({
  children, className = '', heavy = false,
}) => (
  <div
    className={`rounded-3xl border shadow-xl transition-shadow duration-300 ${className}`}
    style={{
      background:         heavy ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.7)',
      backdropFilter:     heavy ? 'blur(40px) saturate(1.4)' : 'blur(24px) saturate(1.2)',
      WebkitBackdropFilter: heavy ? 'blur(40px) saturate(1.4)' : 'blur(24px) saturate(1.2)',
      borderColor:        heavy ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.6)',
      boxShadow:          '0 8px 32px rgba(148,163,184,0.12), 0 1px 3px rgba(148,163,184,0.08)',
    }}
  >
    {children}
  </div>
);

// ─── Reusable Motion Hooks ─────────────────────────────────────
const useAnimatedCounter = (target: number, decimals: number = 0, duration: number = 2000, trigger: boolean = true) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!trigger) return;
    let step = 0;
    const steps = 60;
    const interval = setInterval(() => {
      step++;
      const eased = 1 - Math.pow(1 - step / steps, 3);
      setValue(Number((target * eased).toFixed(decimals)));
      if (step >= steps) clearInterval(interval);
    }, duration / steps);
    return () => clearInterval(interval);
  }, [trigger, target, decimals, duration]);
  return value;
};

// ─── Animated Section Wrapper ─────────────────────────────────
const RevealSection: React.FC<{
  children: React.ReactNode;
  className?: string;
  delay?: number;
  direction?: 'up' | 'left' | 'right';
}> = ({ children, className = '', delay = 0, direction = 'up' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const initial = {
    opacity: 0,
    ...(direction === 'up' ? { y: 40 } : direction === 'left' ? { x: -40 } : { x: 40 }),
  };

  return (
    <motion.div
      ref={ref}
      initial={initial}
      animate={isInView ? { opacity: 1, y: 0, x: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// ─── Magnetic Button ─────────────────────────────────────────
const MagneticButton: React.FC<{
  children: React.ReactNode;
  className?: string;
  href?: string;
  onClick?: () => void;
  style?: React.CSSProperties;
}> = ({ children, className = '', href, onClick, style }) => {
  const ref = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 300, damping: 20 });
  const sy = useSpring(y, { stiffness: 300, damping: 20 });

  const handleMove = (e: React.MouseEvent) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    x.set((e.clientX - r.left - r.width / 2) * 0.12);
    y.set((e.clientY - r.top - r.height / 2) * 0.12);
  };

  const inner = (
    <motion.div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={() => { x.set(0); y.set(0); }}
      style={{ x: sx, y: sy, ...style }}
      className={className}
      onClick={onClick}
    >
      {children}
    </motion.div>
  );

  if (href) return <a href={href}>{inner}</a>;
  return inner;
};

// ─── Animated Pricing Bar Visualization ────────────────────────
const PricingBar: React.FC<{
  label: string;
  value: number;
  maxValue: number;
  color: string;
  delay: number;
  isInView: boolean;
}> = ({ label, value, maxValue, color, delay, isInView }) => {
  const width = (value / maxValue) * 100;
  return (
    <div className="mb-4">
      <div className="flex justify-between mb-1.5">
        <span className="text-sm font-semibold text-slate-600">{label}</span>
        <motion.span
          className="text-sm font-bold text-slate-800"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: delay + 0.5 }}
        >
          HK${value}
        </motion.span>
      </div>
      <div className="h-3 bg-slate-200/60 rounded-full overflow-hidden">
        <motion.div
          className={`h-full rounded-full ${color}`}
          initial={{ width: 0 }}
          animate={isInView ? { width: `${width}%` } : {}}
          transition={{ duration: 1.2, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
        />
      </div>
    </div>
  );
};

// ─── Matching Algorithm Canvas ─────────────────────────────────
const MatchingVisualization: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, { once: true, margin: '-100px' });


  useEffect(() => {
    if (!isInView) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationId: number;
    const w = canvas.offsetWidth;
    const h = canvas.offsetHeight;
    canvas.width = w * 2;
    canvas.height = h * 2;
    ctx.scale(2, 2);

    // Node types
    type Node = {
      x: number; y: number; vx: number; vy: number;
      type: 'rider' | 'driver'; radius: number; glowPhase: number;
    };
    const nodes: Node[] = [];
    for (let i = 0; i < 12; i++) {
      nodes.push({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        type: i < 6 ? 'rider' : 'driver',
        radius: 6 + Math.random() * 3,
        glowPhase: Math.random() * Math.PI * 2,
      });
    }

    let matchPair: [number, number] | null = null;
    let matchProgress = 0;

    // Trigger match every 3s
    const matchInterval = setInterval(() => {
      const riders = nodes.map((n, i) => ({ n, i })).filter(x => x.n.type === 'rider');
      const drivers = nodes.map((n, i) => ({ n, i })).filter(x => x.n.type === 'driver');
      if (riders.length && drivers.length) {
        const r = riders[Math.floor(Math.random() * riders.length)];
        const d = drivers[Math.floor(Math.random() * drivers.length)];
        matchPair = [r.i, d.i];
        matchProgress = 0;
      }
    }, 3000);

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      const time = Date.now() / 1000;

      // Draw connections (proximity)
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 100) {
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(125, 211, 252, ${0.12 * (1 - dist / 100)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      // Draw match arc
      if (matchPair) {
        matchProgress = Math.min(matchProgress + 0.02, 1);
        const a = nodes[matchPair[0]];
        const b = nodes[matchPair[1]];
        const mx = (a.x + b.x) / 2;
        const my = (a.y + b.y) / 2 - 30;
        const progress = matchProgress;

        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        const cp1x = a.x + (mx - a.x) * progress;
        const cp1y = a.y + (my - a.y) * progress;
        const endX = a.x + (b.x - a.x) * progress;
        const endY = a.y + (b.y - a.y) * progress;
        ctx.quadraticCurveTo(cp1x, cp1y, endX, endY);
        ctx.strokeStyle = `rgba(56, 189, 248, ${0.6 * progress})`;
        ctx.lineWidth = 2;
        ctx.stroke();

        if (progress >= 1) {
          // Glow pulse at midpoint
          const glowRadius = 15 + Math.sin(time * 4) * 5;
          const gradient = ctx.createRadialGradient(mx, my + 30, 0, mx, my + 30, glowRadius);
          gradient.addColorStop(0, 'rgba(56, 189, 248, 0.4)');
          gradient.addColorStop(1, 'rgba(56, 189, 248, 0)');
          ctx.beginPath();
          ctx.arc(mx, my + 30, glowRadius, 0, Math.PI * 2);
          ctx.fillStyle = gradient;
          ctx.fill();
        }
      }

      // Draw nodes
      nodes.forEach((node) => {
        node.x += node.vx;
        node.y += node.vy;
        if (node.x < 20 || node.x > w - 20) node.vx *= -1;
        if (node.y < 20 || node.y > h - 20) node.vy *= -1;

        const glow = 0.3 + Math.sin(time * 2 + node.glowPhase) * 0.15;
        const color = node.type === 'rider' ? '56, 189, 248' : '167, 139, 250';

        // Glow
        const grad = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, node.radius * 3);
        grad.addColorStop(0, `rgba(${color}, ${glow})`);
        grad.addColorStop(1, `rgba(${color}, 0)`);
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius * 3, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();

        // Core
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${color}, 0.9)`;
        ctx.fill();
      });

      animationId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animationId);
      clearInterval(matchInterval);
    };
  }, [isInView]);

  return (
    <div ref={containerRef} className="relative w-full h-80 sm:h-96">
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
      <div className="absolute bottom-4 left-4 flex items-center gap-4 text-xs">
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-cyan-400" /> Riders</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-violet-400" /> Drivers</span>
      </div>
    </div>
  );
};

// ─── Radial Progress Indicator ─────────────────────────────────
const RadialProgress: React.FC<{
  value: number;
  label: string;
  color: string;
  isInView: boolean;
}> = ({ value, label, color, isInView }) => {
  const circumference = 2 * Math.PI * 40;
  const offset = circumference - (value / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      <svg width="100" height="100" viewBox="0 0 100 100">
        <circle cx="50" cy="50" r="40" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="6" />
        <motion.circle
          cx="50" cy="50" r="40" fill="none"
          stroke={color}
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={isInView ? { strokeDashoffset: offset } : {}}
          transition={{ duration: 1.5, ease: [0.25, 0.46, 0.45, 0.94] }}
          transform="rotate(-90 50 50)"
        />
        <text x="50" y="50" textAnchor="middle" dominantBaseline="central"
          className="text-lg font-bold" fill={T.text}>{value}%</text>
      </svg>
      <span className="text-sm text-slate-500 mt-2">{label}</span>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAIN FEATURES PAGE
// ═══════════════════════════════════════════════════════════════
const Features: React.FC = () => {
  const { scrollYProgress } = useScroll();
  const [activeView, setActiveView] = useState<'rider' | 'driver'>('rider');
  const pricingRef = useRef(null);
  const pricingInView = useInView(pricingRef, { once: true, margin: '-100px' });
  const statsRef = useRef(null);
  const statsInView = useInView(statsRef, { once: true });

  const matchTime = useAnimatedCounter(8, 0, 2000, statsInView);
  const savings = useAnimatedCounter(25, 0, 2000, statsInView);
  const driverPayout = useAnimatedCounter(95, 0, 2000, statsInView);

  // Parallax for hero
  const heroY = useTransform(scrollYProgress, [0, 0.1], [0, -60]);

  const coreFeatures = [
    { icon: '⚡', title: 'Lightning Matching', desc: 'AI connects you with the nearest driver in under 10 seconds. Zero wait anxiety.', accent: T.teal },
    { icon: '📍', title: 'Real-Time GPS', desc: 'Precision tracking powered by Gaode Maps. Share your location with anyone, instantly.', accent: T.lavender },
    { icon: '💬', title: 'In-App Communication', desc: 'Chat or call your driver without sharing personal numbers. Encrypted end-to-end.', accent: T.coral },
    { icon: '⭐', title: 'Two-Way Profiles', desc: 'Ratings, reviews, trip history. Full transparency for riders and drivers alike.', accent: T.teal },
    { icon: '🔔', title: 'Smart Notifications', desc: 'Context-aware alerts. Ride requests, driver arrivals, route changes — nothing missed.', accent: T.lavender },
    { icon: '🛡️', title: 'Advanced Safety', desc: 'Driver verification, emergency SOS, 24/7 monitoring, ride recording.', accent: T.coral },
  ];

  return (
    <div className="relative z-10 text-slate-800">

      {/* ═══ SCENE 1: HERO ═══ */}
      <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden px-4 sm:px-6">
        <motion.div style={{ y: heroY }} className="relative z-10 max-w-5xl mx-auto text-center">
          <RevealSection>
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <div
                className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 mb-8 border"
                style={{
                  background: 'rgba(255,255,255,0.7)',
                  backdropFilter: 'blur(16px)',
                  borderColor: 'rgba(255,255,255,0.6)',
                }}
              >
                <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: T.teal }} />
                <span className="text-slate-500 text-sm font-medium tracking-wide">Platform Capabilities</span>
              </div>
            </motion.div>
          </RevealSection>

          <RevealSection delay={0.2}>
            <h1 className="text-5xl sm:text-7xl lg:text-8xl font-black leading-[0.9] tracking-tight mb-8 text-slate-800">
              Engineered for
              <br />
              <span className="bg-gradient-to-r from-teal-400 via-violet-400 to-rose-400 bg-clip-text text-transparent">
                every detail.
              </span>
            </h1>
          </RevealSection>

          <RevealSection delay={0.4}>
            <p className="text-lg sm:text-xl text-slate-500 max-w-2xl mx-auto leading-relaxed">
              From the algorithm that matches you, to the pixel that shows your route —
              every feature exists because it has to.
            </p>
          </RevealSection>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
            className="w-6 h-10 border-2 border-slate-300/50 rounded-full flex items-start justify-center p-1.5"
          >
            <motion.div className="w-1 h-1 bg-slate-400 rounded-full" />
          </motion.div>
        </motion.div>
      </section>

      {/* ═══ SCENE 2: STATS STRIP ═══ */}
      <section ref={statsRef} className="relative py-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <RevealSection delay={0}>
              <GlassCard className="p-8">
                <p className="text-5xl sm:text-6xl font-black text-slate-800 mb-1">{matchTime}s</p>
                <p className="text-slate-500 font-medium">Average match time</p>
              </GlassCard>
            </RevealSection>
            <RevealSection delay={0.15}>
              <GlassCard className="p-8">
                <p className="text-5xl sm:text-6xl font-black text-slate-800 mb-1">{savings}%</p>
                <p className="text-slate-500 font-medium">Cheaper than competitors</p>
              </GlassCard>
            </RevealSection>
            <RevealSection delay={0.3}>
              <GlassCard className="p-8">
                <p className="text-5xl sm:text-6xl font-black text-slate-800 mb-1">{driverPayout}%</p>
                <p className="text-slate-500 font-medium">Driver earnings kept</p>
              </GlassCard>
            </RevealSection>
          </div>
        </div>
      </section>

      {/* ═══ SCENE 3: CORE FEATURES ═══ */}
      <section className="relative py-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <RevealSection className="text-center mb-16">
            <p className="text-sm font-bold tracking-[0.2em] uppercase mb-4" style={{ color: T.teal }}>Core Features</p>
            <h2 className="text-4xl sm:text-5xl font-black leading-tight text-slate-800">
              Built different.
              <br />
              <span className="text-slate-400">By design.</span>
            </h2>
          </RevealSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {coreFeatures.map((feat, i) => (
              <RevealSection key={i} delay={i * 0.08}>
                <motion.div whileHover={{ y: -4, transition: { duration: 0.3 } }}>
                  <GlassCard className="p-7 hover:shadow-2xl">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center mb-5 text-xl"
                      style={{ background: `${feat.accent}18` }}
                    >
                      {feat.icon}
                    </div>
                    <h3 className="text-lg font-bold text-slate-800 mb-2">{feat.title}</h3>
                    <p className="text-slate-500 text-sm leading-relaxed">{feat.desc}</p>
                  </GlassCard>
                </motion.div>
              </RevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SCENE 4: HOW IT WORKS ═══ */}
      <section className="relative py-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <RevealSection className="text-center mb-20">
            <p className="text-sm font-bold tracking-[0.2em] uppercase mb-4" style={{ color: T.lavender }}>How It Works</p>
            <h2 className="text-4xl sm:text-5xl font-black leading-tight text-slate-800">
              Four steps.
              <br />
              <span className="text-slate-400">Zero friction.</span>
            </h2>
          </RevealSection>

          <div className="relative">
            {/* Connection line */}
            <div
              className="hidden lg:block absolute top-24 left-[12.5%] right-[12.5%] h-px"
              style={{ background: `linear-gradient(90deg, transparent, ${T.teal}40, transparent)` }}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { step: '01', icon: '📍', title: 'Set Pickup', desc: 'Drop a pin or type. Smart autocomplete knows every corner of HK.' },
                { step: '02', icon: '🚗', title: 'Choose Tier', desc: 'Economy, Comfort, or Premium. See exact fare upfront.' },
                { step: '03', icon: '🤝', title: 'Get Matched', desc: 'Nearest verified driver found. Average: under 3 minutes.' },
                { step: '04', icon: '🎉', title: 'Enjoy & Rate', desc: 'Track live, chat en route. Rate when you arrive.' },
              ].map((item, i) => (
                <RevealSection key={i} delay={i * 0.12}>
                  <div className="relative text-center group">
                    <motion.div whileHover={{ scale: 1.05 }} className="relative mx-auto w-20 h-20 mb-6">
                      <GlassCard heavy className="!rounded-2xl w-20 h-20 flex items-center justify-center">
                        <span className="text-3xl">{item.icon}</span>
                      </GlassCard>
                      <span
                        className="absolute -top-2 -right-2 w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-black text-white"
                        style={{ background: T.teal }}
                      >
                        {item.step}
                      </span>
                    </motion.div>
                    <h3 className="text-lg font-bold text-slate-800 mb-2">{item.title}</h3>
                    <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </RevealSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SCENE 5: MATCHING ALGORITHM VISUALIZATION ═══ */}
      <section className="relative py-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <RevealSection direction="left">
              <p className="text-sm font-bold tracking-[0.2em] uppercase mb-4" style={{ color: T.teal }}>Matching Engine</p>
              <h2 className="text-4xl sm:text-5xl font-black leading-tight mb-6 text-slate-800">
                Intelligence<br />
                <span className="text-slate-400">in motion.</span>
              </h2>
              <p className="text-slate-500 leading-relaxed mb-6">
                Our matching algorithm considers distance, driver rating, vehicle type, traffic conditions,
                and predicted route efficiency — all computed in under a second.
              </p>
              <div className="space-y-4">
                {[
                  'Spatial indexing for O(log n) driver lookup',
                  'Traffic-aware ETA prediction',
                  'Dynamic supply-demand balancing',
                  'Driver preference learning',
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <span
                      className="w-5 h-5 rounded flex items-center justify-center text-xs flex-shrink-0"
                      style={{ background: `${T.teal}18`, color: T.tealDark }}
                    >✓</span>
                    <span className="text-slate-600 text-sm">{item}</span>
                  </motion.div>
                ))}
              </div>
            </RevealSection>

            <RevealSection direction="right">
              <GlassCard className="overflow-hidden">
                <MatchingVisualization />
              </GlassCard>
            </RevealSection>
          </div>
        </div>
      </section>

      {/* ═══ SCENE 6: ANIMATED PRICING ENGINE ═══ */}
      <section ref={pricingRef} className="relative py-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <RevealSection className="text-center mb-16">
            <p className="text-sm font-bold tracking-[0.2em] uppercase mb-4" style={{ color: T.coral }}>Transparent Pricing</p>
            <h2 className="text-4xl sm:text-5xl font-black leading-tight text-slate-800">
              Fair for everyone.
              <br />
              <span className="text-slate-400">No surprises.</span>
            </h2>
          </RevealSection>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Pricing comparison bars */}
            <RevealSection direction="left">
              <GlassCard heavy className="p-8">
                <h3 className="text-lg font-bold text-slate-800 mb-1">10km ride comparison</h3>
                <p className="text-slate-400 text-sm mb-8">Cross Harbour Tunnel route</p>

                <div className="space-y-1 mb-8">
                  <p className="text-xs font-bold uppercase tracking-wider mb-4" style={{ color: T.teal }}>Karvlo</p>
                  <PricingBar label="Economy" value={35} maxValue={120} color="bg-gradient-to-r from-teal-400 to-blue-400" delay={0} isInView={pricingInView} />
                  <PricingBar label="Comfort" value={55} maxValue={120} color="bg-gradient-to-r from-blue-400 to-violet-400" delay={0.15} isInView={pricingInView} />
                  <PricingBar label="Premium" value={85} maxValue={120} color="bg-gradient-to-r from-violet-400 to-purple-400" delay={0.3} isInView={pricingInView} />
                </div>

                <div className="space-y-1">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Competitors (avg)</p>
                  <PricingBar label="Standard" value={65} maxValue={120} color="bg-slate-300" delay={0.5} isInView={pricingInView} />
                  <PricingBar label="Premium" value={110} maxValue={120} color="bg-slate-300" delay={0.65} isInView={pricingInView} />
                </div>
              </GlassCard>
            </RevealSection>

            {/* Pricing tier cards */}
            <RevealSection direction="right">
              <div className="space-y-4">
                {[
                  { tier: 'Economy', price: 'HK$35', desc: 'Everyday commutes', features: ['Standard sedan', 'Up to 4 pax', '2 luggage', 'Fastest matching'], accent: T.teal, badge: '' },
                  { tier: 'Comfort', price: 'HK$55', desc: 'Extra space & comfort', features: ['Premium sedan', 'Up to 4 pax', '3 luggage', 'Wi-Fi available'], accent: T.lavender, badge: 'POPULAR' },
                  { tier: 'Premium', price: 'HK$85', desc: 'Luxury, every ride', features: ['Luxury vehicle', 'Up to 4 pax', 'Priority match', 'Refreshments'], accent: T.coral, badge: '' },
                ].map((plan, i) => (
                  <motion.div key={i} whileHover={{ x: 4 }} className="relative">
                    {plan.badge && (
                      <span
                        className="absolute -top-2.5 right-4 text-white text-[10px] font-black px-2.5 py-0.5 rounded-full z-10"
                        style={{ background: plan.accent }}
                      >
                        {plan.badge}
                      </span>
                    )}
                    <GlassCard className="p-5 flex items-center gap-5">
                      <div className="flex-shrink-0 text-center min-w-[60px]">
                        <p className="text-xl font-black text-slate-800 leading-tight">{plan.price}</p>
                        <p className="text-[10px] text-slate-400">from</p>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-slate-800">{plan.tier}</p>
                        <p className="text-slate-500 text-sm">{plan.desc}</p>
                      </div>
                      <div className="hidden sm:flex flex-wrap gap-1.5 max-w-[180px]">
                        {plan.features.map((f, j) => (
                          <span
                            key={j}
                            className="text-[10px] font-medium px-2 py-0.5 rounded-md"
                            style={{ background: `${plan.accent}15`, color: plan.accent }}
                          >
                            {f}
                          </span>
                        ))}
                      </div>
                    </GlassCard>
                  </motion.div>
                ))}
              </div>
            </RevealSection>
          </div>
        </div>
      </section>

      {/* ═══ SCENE 7: RIDER ↔ DRIVER MORPH ═══ */}
      <section className="relative py-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <RevealSection className="text-center mb-16">
            <p className="text-sm font-bold tracking-[0.2em] uppercase mb-4" style={{ color: T.lavender }}>Two Perspectives</p>
            <h2 className="text-4xl sm:text-5xl font-black leading-tight mb-8 text-slate-800">
              One platform.
              <br />
              <span className="text-slate-400">Two experiences.</span>
            </h2>

            {/* Toggle */}
            <div
              className="inline-flex rounded-xl p-1 border"
              style={{
                background: 'rgba(255,255,255,0.7)',
                backdropFilter: 'blur(16px)',
                borderColor: 'rgba(255,255,255,0.6)',
              }}
            >
              {(['rider', 'driver'] as const).map(view => (
                <button
                  key={view}
                  onClick={() => setActiveView(view)}
                  className={`relative px-8 py-3 rounded-lg text-sm font-semibold transition-all duration-300 ${
                    activeView === view ? 'text-slate-800' : 'text-slate-400 hover:text-slate-600'
                  }`}
                >
                  {activeView === view && (
                    <motion.div
                      layoutId="viewToggle"
                      className="absolute inset-0 rounded-lg"
                      style={{
                        background: `${T.teal}15`,
                        border: `1px solid ${T.teal}30`,
                      }}
                      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                    />
                  )}
                  <span className="relative z-10 capitalize">{view}</span>
                </button>
              ))}
            </div>
          </RevealSection>

          <AnimatePresence mode="wait">
            {activeView === 'rider' ? (
              <motion.div
                key="rider"
                initial={{ opacity: 0, y: 20, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.97 }}
                transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="grid grid-cols-1 md:grid-cols-3 gap-6"
              >
                {[
                  { icon: '📱', title: 'Book in Seconds', desc: 'Set pickup, choose tier, confirm. Three taps maximum.', stat: '3 taps', accent: T.teal },
                  { icon: '📡', title: 'Live Tracking', desc: 'Watch your driver approach in real-time on the map.', stat: 'Real-time', accent: T.lavender },
                  { icon: '💳', title: 'Easy Payment', desc: 'Automatic payment. Split fares with friends.', stat: '1-tap pay', accent: T.coral },
                ].map((card, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <GlassCard className="p-7 h-full">
                      <span className="text-3xl mb-4 block">{card.icon}</span>
                      <h3 className="text-lg font-bold text-slate-800 mb-2">{card.title}</h3>
                      <p className="text-slate-500 text-sm leading-relaxed mb-4">{card.desc}</p>
                      <span className="text-xs font-black uppercase tracking-wider" style={{ color: card.accent }}>{card.stat}</span>
                    </GlassCard>
                  </motion.div>
                ))}
              </motion.div>
            ) : (
              <motion.div
                key="driver"
                initial={{ opacity: 0, y: 20, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.97 }}
                transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  {[
                    { icon: '💵', title: 'Higher Earnings', desc: 'Keep 95% of every fare. Weekly payouts, zero hidden fees.', stat: '95%', accent: T.teal },
                    { icon: '📊', title: 'Analytics Dashboard', desc: 'Track earnings, ratings, hotspots, and peak hours.', stat: 'Real-time', accent: T.lavender },
                    { icon: '🏆', title: 'Rewards & Bonuses', desc: 'Milestones, streaks, peak multipliers, and loyalty tiers.', stat: 'Gamified', accent: T.coral },
                  ].map((card, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.1 }}
                    >
                      <GlassCard className="p-7 h-full">
                        <span className="text-3xl mb-4 block">{card.icon}</span>
                        <h3 className="text-lg font-bold text-slate-800 mb-2">{card.title}</h3>
                        <p className="text-slate-500 text-sm leading-relaxed mb-4">{card.desc}</p>
                        <span className="text-xs font-black uppercase tracking-wider" style={{ color: card.accent }}>{card.stat}</span>
                      </GlassCard>
                    </motion.div>
                  ))}
                </div>

                {/* Driver dashboard metrics */}
                <div ref={statsRef}>
                  <GlassCard heavy className="p-8">
                    <div className="flex flex-wrap justify-center gap-8">
                      <RadialProgress value={92} label="Acceptance" color={T.teal} isInView={statsInView} />
                      <RadialProgress value={97} label="Completion" color={T.lavender} isInView={statsInView} />
                      <RadialProgress value={88} label="On-time" color={T.coral} isInView={statsInView} />
                      <RadialProgress value={95} label="Satisfaction" color={T.tealDark} isInView={statsInView} />
                    </div>
                  </GlassCard>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* ═══ SCENE 8: CTA ═══ */}
      <section className="relative py-28 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto text-center">
          <RevealSection>
            <h2 className="text-4xl sm:text-5xl font-black leading-tight mb-6 text-slate-800">
              Ready to
              <br />
              <span className="bg-gradient-to-r from-teal-400 via-violet-400 to-rose-400 bg-clip-text text-transparent">
                experience it?
              </span>
            </h2>
            <p className="text-slate-500 text-lg mb-10 max-w-xl mx-auto">
              Join the beta. Your first ride is on us.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <MagneticButton
                href="/#scene-signup"
                className="inline-flex items-center justify-center px-8 py-4 bg-slate-800 rounded-2xl text-white font-bold text-lg shadow-lg shadow-slate-300/40 hover:shadow-xl hover:bg-slate-700 transition-all duration-300 cursor-pointer"
              >
                Join the Beta
              </MagneticButton>
              <MagneticButton
                href="https://app.karvlo.com"
                className="inline-flex items-center justify-center px-8 py-4 rounded-2xl font-semibold text-lg text-slate-700 cursor-pointer"
                style={{ background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.6)' } as React.CSSProperties}
              >
                Open App →
              </MagneticButton>
            </div>
          </RevealSection>
        </div>
      </section>
    </div>
  );
};

export default Features;

